/**
 * Locale-aware display formatting. Presentation only — no engineering math.
 *
 * IMPORTANT: unit *conversion* no longer lives here. Every measurement is
 * stored as-entered in its record's unit-system profile and converted on the
 * way out by the `@units` module (`useUnits`), which resolves the field's
 * quantity class → stored unit → the viewer's display unit and labels it
 * dynamically. The old locale-guessed metres→feet transform was exactly the
 * m/ft bug (it inflated feet-native wells ×3.28); it has been removed.
 *
 * What remains here is pure locale number/percent/money/hours formatting.
 */
import type { Locale, UnitSystem } from "@i18n/locales";
import { LOCALE_META } from "@i18n/locales";

/** The Intl locale tag we hand to toLocaleString for a given app locale. */
export function intlTag(locale: Locale): string {
  return locale === "en" ? "en-US" : locale === "es" ? "es-AR" : "pt-BR";
}

export function unitSystem(locale: Locale): UnitSystem {
  return LOCALE_META[locale].defaultUnitSystem;
}

/**
 * Fallback depth formatter for a value already known to be in **metres** and
 * carrying no unit-system profile. Prefer `useUnits().convertField(...)` for
 * any profile-carrying value; this only labels metres honestly (no conversion,
 * no locale guess). Kept for callers with a plain metres number and no profile.
 */
export function fmtDepthMetres(metres: number | null | undefined, locale: Locale): string {
  if (metres == null || !Number.isFinite(metres)) return "—";
  return `${Math.round(metres).toLocaleString(intlTag(locale))} m`;
}

/** Format a plain integer / count with locale grouping. */
export function fmtNum(
  n: number | null | undefined,
  locale: Locale,
  opts?: Intl.NumberFormatOptions,
): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(intlTag(locale), opts);
}

/**
 * Format a money amount. Values are served already in the tenant's reporting
 * currency; we only format. Compact ($8.4M) when `compact` is set.
 */
export function fmtMoney(
  amount: number | null | undefined,
  locale: Locale,
  opts?: { compact?: boolean; currency?: string },
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const currency = opts?.currency ?? "USD";
  return amount.toLocaleString(intlTag(locale), {
    style: "currency",
    currency,
    notation: opts?.compact ? "compact" : "standard",
    maximumFractionDigits: opts?.compact ? 2 : 0,
  });
}

/** Format a signed variance amount with an explicit +/- and colour class. */
export function fmtSignedMoney(
  amount: number | null | undefined,
  locale: Locale,
  currency = "USD",
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
  const abs = Math.abs(amount).toLocaleString(intlTag(locale), {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
  return `${sign}${abs}`;
}

/** Percent formatting for a 0–100 value (or null). */
export function fmtPct(pct: number | null | undefined, locale: Locale, digits = 0): string {
  if (pct == null || !Number.isFinite(pct)) return "—";
  return `${pct.toLocaleString(intlTag(locale), { maximumFractionDigits: digits })}%`;
}

/** Hours with one decimal. */
export function fmtHours(h: number | null | undefined, locale: Locale): string {
  if (h == null || !Number.isFinite(h)) return "—";
  return `${h.toLocaleString(intlTag(locale), { maximumFractionDigits: 1 })} h`;
}
